import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface BalanceData {
  userId: string;
  refereeCode: string;
  FullName: string;
  ngnBalance: string;
  usdBalance: string;
  eurBalance: string;
  gbpBalance: string;
  usdtBalance: string;
  usdcBalance: string;
  ngnCommissionBalance: string;
}

interface BalanceTableProps {
  data?: {
    data: BalanceData[];
    page: number;
    pageSize: number;
    total: string;
    totalPages: number;
  };
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  currentTab: string
  searchTerm?: string;
}

const formatCurrency = (amount: string | undefined, decimals: number = 2) => {
  if (!amount || isNaN(Number(amount))) {
    return "0.00";
  }
  return Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};


const BalanceTable = ({
  data,
  isLoading,
  page,
  onPageChange,
  currentTab,
  searchTerm = "",
}: BalanceTableProps) => {
  const handlePrevPage = () => {
    onPageChange(Math.max(1, page - 1));
  };

  const handleNextPage = () => {
    if (data && page < data.totalPages) {
      onPageChange(page + 1);
    }
  };

  // Filter data based on search term
  const filteredData = data?.data.filter(
    (item) =>
      item.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.refereeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.FullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const columns =
    currentTab === "fiat"
      ? [
          // { key: "userId", label: "USER ID", align: "left" },
          { key: "refereeCode", label: "Referee Code", align: "left" },
          { key: "fullName", label: "Full Name", align: "left" },
          { key: "ngnBalance", label: "NGN Balance", align: "right" },
          { key: "usdBalance", label: "USD Balance", align: "right" },
          { key: "gbpBalance", label: "GBP Balance", align: "right" },
          { key: "eurBalance", label: "EUR Balance", align: "right" },
          { key: "ngnCommission", label: "NGN Commission", align: "right" },
        ]
      : [
          // { key: "userId", label: "USER ID", align: "left" },
          { key: "refereeCode", label: "Referee Code", align: "left" },
          { key: "fullName", label: "Full Name", align: "left" },
          { key: "usdcBalance", label: "USDC Balance", align: "right" },
          { key: "usdtBalance", label: "USDT Balance", align: "right" },
        ];

  return (
    <div className="bg-white rounded-lg shadow-sm w-full">
      <div className="overflow-x-auto">
        <table className="appearance-none bg-white min-w-full  mb-6">
          <thead className="bg-[#fff] appearance-none">
            <tr className="border-b bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`font-normal text-sm text-primary py-4  whitespace-nowrap px-4 border-r border-slate-50 ${
                    column.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  <span className="text-[#5C6F7F] font-bold text-[12px] leading-[18px]">
                    {column.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="mt-3 pt-3 w-full space-y-8 border-r border-slate-50">
            {(filteredData || []).map((balance: BalanceData) => (
              <tr
                key={balance.userId}
                className="appearance-none my-4 border border-slate-50 even:bg-white odd:bg-slate-100"
              >
                {/* <td className="py-3 px-4 text-sm max-w-[190px] text-[#0eb683] underline cursor-pointer">
                  <div className="truncate" title={balance.userId}>
                    {balance.userId}
                  </div>
                </td> */}
                <td className="font-normal text-sm text-[#202223] py-4 px-4">
                  {balance.refereeCode}
                </td>
                <td className="font-normal text-sm text-[#202223] py-4 px-4">
                  {balance.FullName}
                </td>
                {currentTab === "fiat" ? (
                  <>
                    <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right">
                      ₦ {formatCurrency(balance.ngnBalance)}
                    </td>
                    <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right">
                      $ {formatCurrency(balance.usdBalance)}
                    </td>
                    <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right">
                      £ {formatCurrency(balance.gbpBalance)}
                    </td>
                    <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right">
                      € {formatCurrency(balance.eurBalance)}
                    </td>
                    <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right">
                      ₦ {formatCurrency(balance.ngnCommissionBalance)}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right">
                      {formatCurrency(balance.usdcBalance)} USDC
                    </td>
                    <td className="font-normal text-sm text-[#202223] py-4 px-4 text-right">
                      {formatCurrency(balance.usdtBalance)} USDT
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between p-4 border-t">
        <div className="text-sm text-gray-500">
          Page {page} of {data?.totalPages} ({data?.total} total records)
        </div>
        <div className="flex gap-2">
          <button
            className={`flex items-center px-3 py-1 text-sm border rounded-md ${
              page <= 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={handlePrevPage}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </button>
          <button
            className={`flex items-center px-3 py-1 text-sm border rounded-md ${
              !data || page >= data.totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
            onClick={handleNextPage}
            disabled={!data || page >= data.totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BalanceTable;
